import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  Search, Eye, EyeOff, RefreshCw, LogOut, Database, Activity,
  DollarSign, TrendingUp, Calendar, BarChart3, Snowflake, X, ChevronRight, ArrowUpRight, Clock,
  CheckCircle2, AlertCircle, XCircle, RefreshCcw, Wallet, Layers,
  Zap, Menu
} from 'lucide-react';
import '../styles/App.css';

// Modern color palette
const THEME = {
  bg: {
    primary: '#0a0a0f',
    secondary: '#12121a',
    tertiary: '#1a1a25',
    card: 'rgba(26, 26, 37, 0.6)',
  },
  accent: {
    primary: '#6366f1',    // Indigo
    secondary: '#8b5cf6',  // Purple
    success: '#10b981',    // Emerald
    warning: '#f59e0b',    // Amber
    danger: '#ef4444',     // Red
    info: '#06b6d4',       // Cyan
    frost: '#67e8f9',      // Ice blue
  },
  text: {
    primary: '#f8fafc',
    secondary: '#94a3b8',
    muted: '#64748b',
  },
  border: 'rgba(255, 255, 255, 0.08)',
  glow: {
    primary: '0 0 20px rgba(99, 102, 241, 0.3)',
    success: '0 0 20px rgba(16, 185, 129, 0.3)',
    warning: '0 0 20px rgba(245, 158, 11, 0.3)',
    danger: '0 0 20px rgba(239, 68, 68, 0.3)',
  }
};

const STATUS_CONFIG = {
  completed: { color: THEME.accent.success, icon: CheckCircle2, label: 'Completed', glow: THEME.glow.success },
  user_deposit_pending: { color: THEME.accent.warning, icon: Clock, label: 'Pending', glow: THEME.glow.warning },
  pending: { color: THEME.accent.warning, icon: Clock, label: 'Pending', glow: THEME.glow.warning },
  waiting: { color: THEME.accent.info, icon: Wallet, label: 'Waiting', glow: '0 0 20px rgba(6, 182, 212, 0.3)' },
  confirming: { color: THEME.accent.info, icon: RefreshCcw, label: 'Confirming', glow: '0 0 20px rgba(6, 182, 212, 0.3)' },
  exchanging: { color: THEME.accent.primary, icon: Zap, label: 'Exchanging', glow: THEME.glow.primary },
  sending: { color: THEME.accent.secondary, icon: ArrowUpRight, label: 'Sending', glow: '0 0 20px rgba(139, 92, 246, 0.3)' },
  failed: { color: THEME.accent.danger, icon: XCircle, label: 'Failed', glow: THEME.glow.danger },
  refunded: { color: THEME.text.muted, icon: RefreshCcw, label: 'Refunded', glow: 'none' },
  expired: { color: THEME.text.muted, icon: AlertCircle, label: 'Expired', glow: 'none' },
  archived: { color: '#8b5cf6', icon: Database, label: 'Archived', glow: 'none' },
};

// Provider configuration with colors and icons
const PROVIDER_CONFIG = {
  '1': { name: 'StealthEX', color: '#6366f1', icon: '🔷' },
  '2': { name: '1Click', color: '#8b5cf6', icon: '💜' },
  '3': { name: 'SplitNow', color: '#10b981', icon: '💚' },
  '4': { name: 'Exolix', color: '#f59e0b', icon: '🟠' },
  '5': { name: 'ChangeNOW', color: '#06b6d4', icon: '🔵' },
  '6': { name: 'SwapKit', color: '#ec4899', icon: '🩷' },
  '7': { name: 'Baltex', color: '#84cc16', icon: '🟢' },
  '8': { name: 'BitcoinVN', color: '#f97316', icon: '🟧' },
};

const API_BASE = process.env.REACT_APP_API_URL || '/api';

const isOrderActive = (status) =>
  status !== 'completed' && status !== 'failed' && status !== 'refunded' && status !== 'expired';

const formatCurrency = (value) => {
  if (!value) return '$0.00';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
};

const providerLabel = (providerId) => {
  return PROVIDER_CONFIG[providerId]?.name || `Provider ${providerId}`;
};

const providerColor = (providerId) => {
  return PROVIDER_CONFIG[providerId]?.color || THEME.accent.primary;
};

const providerIcon = (providerId) => {
  return PROVIDER_CONFIG[providerId]?.icon || '🔹';
};

// Animated counter component
const AnimatedCounter = ({ value, duration = 1000 }) => {
  const [displayValue, setDisplayValue] = useState(0);
  
  useEffect(() => {
    const start = displayValue;
    const end = value;
    const startTime = performance.now();
    
    const animate = (currentTime) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const easeOut = 1 - Math.pow(1 - progress, 3);
      const current = Math.floor(start + (end - start) * easeOut);
      
      setDisplayValue(current);
      
      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };
    
    requestAnimationFrame(animate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, duration]);
  
  return <span>{displayValue.toLocaleString()}</span>;
};

const AdminDashboard = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const [orders, setOrders] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchError, setSearchError] = useState('');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState({ total_orders: 0, total_quotes: 0 });
  const [logs, setLogs] = useState([]);
  const [volumeStats, setVolumeStats] = useState(null);
  const [activeTab, setActiveTab] = useState('orders');
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const authHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem('admin_token')}`,
  });

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setLoginError('');

    try {
      const res = await fetch(`${API_BASE}/admin/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password }),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setLoginError(data.detail || 'Invalid password. Please try again.');
        return;
      }

      const data = await res.json();
      localStorage.setItem('admin_token', data.access_token);
      setIsAuthenticated(true);
      fetchData();
    } catch (err) {
      setLoginError('Login failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    localStorage.removeItem('admin_token');
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [ordersRes, statsRes, logsRes] = await Promise.all([
        fetch(`${API_BASE}/admin/orders?limit=100`, { headers: authHeaders() }),
        fetch(`${API_BASE}/admin/stats`, { headers: authHeaders() }),
        fetch(`${API_BASE}/admin/logs?limit=50`, { headers: authHeaders() })
      ]);

      if (!ordersRes.ok) throw new Error('Failed to fetch orders');

      const ordersData = await ordersRes.json();
      setOrders(ordersData || []);
      setLastUpdated(new Date());

      if (statsRes.ok) {
        const statsData = await statsRes.json();
        setStats(statsData || { total_orders: 0, total_quotes: 0 });
      }

      if (logsRes.ok) {
        const logsData = await logsRes.json();
        setLogs(logsData || []);
      }

      try {
        const volumeRes = await fetch(`${API_BASE}/admin/volume`, { headers: authHeaders() });
        if (volumeRes.ok) {
          const volumeData = await volumeRes.json();
          setVolumeStats(volumeData);
        }
      } catch (volErr) {
        console.log('Volume stats not available:', volErr);
      }
    } catch (err) {
      console.error('Failed to fetch data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  const refreshOrders = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/admin/orders?limit=100`, { headers: authHeaders() });
      if (res.ok) {
        const data = await res.json();
        setOrders(data);
        setLastUpdated(new Date());
      }
    } catch (err) {
      console.error('Failed to refresh orders:', err);
    }
  }, []);

  const refreshSingleOrder = useCallback(
    async (orderId) => {
      try {
        const res = await fetch(`${API_BASE}/admin/refresh-status/${orderId}`, {
          method: 'POST',
          headers: authHeaders(),
        });
        if (res.ok) {
          const updatedOrder = await res.json();
          if (selectedOrder && selectedOrder.id === orderId) {
            setSelectedOrder(updatedOrder);
          }
          setOrders((prev) => prev.map((o) => (o.id === orderId ? updatedOrder : o)));
        }
      } catch (err) {
        console.error('Failed to refresh order:', err);
      }
    },
    [selectedOrder]
  );

  const deleteOrder = async (orderId, e) => {
    e?.stopPropagation();
    if (!window.confirm(`Delete order ${orderId.slice(0, 8)}... from database?`)) return;
    
    try {
      const res = await fetch(`${API_BASE}/admin/orders/${orderId}`, {
        method: 'DELETE',
        headers: authHeaders(),
      });
      
      if (res.ok) {
        setOrders(prev => prev.filter(o => o.id !== orderId));
        if (selectedOrder?.id === orderId) setSelectedOrder(null);
      } else {
        alert('Failed to delete order');
      }
    } catch (err) {
      console.error('Delete failed:', err);
    }
  };

  const changeOrderStatus = async (orderId, newStatus, e) => {
    e?.stopPropagation();
    if (!window.confirm(`Change order status to ${newStatus}?`)) return;
    
    try {
      const res = await fetch(`${API_BASE}/admin/orders/${orderId}/status`, {
        method: 'PATCH',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus }),
      });
      
      if (res.ok) {
        setOrders(prev => prev.map(o => o.id === orderId ? { ...o, status: newStatus } : o));
        if (selectedOrder?.id === orderId) {
          setSelectedOrder(prev => ({ ...prev, status: newStatus }));
        }
      } else {
        alert('Failed to update status');
      }
    } catch (err) {
      console.error('Status update failed:', err);
    }
  };

  const searchOrder = async () => {
    if (!searchQuery.trim()) return;
    setLoading(true);
    setSearchError('');
    try {
      const res = await fetch(`${API_BASE}/admin/orders/${searchQuery}`, { headers: authHeaders() });
      if (res.ok) {
        const data = await res.json();
        setSelectedOrder(data);
      } else {
        setSearchError(`No order found matching "${searchQuery}"`);
      }
    } catch (err) {
      setSearchError('Search failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const viewOrderDetails = async (orderId) => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/admin/orders/${orderId}`, { headers: authHeaders() });
      const data = await res.json();
      setSelectedOrder(data);
    } catch (err) {
      console.error('Failed to fetch order:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isAuthenticated || activeTab !== 'orders') return;
    const interval = setInterval(refreshOrders, 10000);
    return () => clearInterval(interval);
  }, [isAuthenticated, activeTab, refreshOrders]);

  useEffect(() => {
    if (!selectedOrder) return;
    const interval = setInterval(() => refreshSingleOrder(selectedOrder.id), 5000);
    return () => clearInterval(interval);
  }, [selectedOrder, refreshSingleOrder]);

  useEffect(() => {
    const auth = localStorage.getItem('admin_auth');
    if (auth === 'true') {
      setIsAuthenticated(true);
      fetchData();
    }
  }, [fetchData]);

  if (!isAuthenticated) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: `linear-gradient(135deg, ${THEME.bg.primary} 0%, #0f172a 50%, ${THEME.bg.secondary} 100%)`,
        position: 'relative',
        overflow: 'hidden',
      }}>
        <div style={{
          position: 'absolute',
          width: '600px',
          height: '600px',
          background: 'radial-gradient(circle, rgba(99, 102, 241, 0.15) 0%, transparent 70%)',
          top: '-200px',
          right: '-200px',
          borderRadius: '50%',
          filter: 'blur(60px)',
        }} />
        <div style={{
          position: 'absolute',
          width: '400px',
          height: '400px',
          background: 'radial-gradient(circle, rgba(139, 92, 246, 0.1) 0%, transparent 70%)',
          bottom: '-100px',
          left: '-100px',
          borderRadius: '50%',
          filter: 'blur(40px)',
        }} />

        <div style={{
          width: '100%',
          maxWidth: '420px',
          margin: '20px',
          padding: '48px',
          background: THEME.bg.card,
          backdropFilter: 'blur(20px)',
          borderRadius: '24px',
          border: `1px solid ${THEME.border}`,
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
          position: 'relative',
          zIndex: 1,
        }}>
          <div style={{
            width: '64px',
            height: '64px',
            borderRadius: '16px',
            background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: '32px',
            boxShadow: THEME.glow.primary,
          }}>
            <Snowflake size={32} color="white" />
          </div>

          <h1 style={{
            fontSize: '28px',
            fontWeight: '700',
            color: THEME.text.primary,
            marginBottom: '8px',
            letterSpacing: '-0.5px',
          }}>
            Admin Access
          </h1>
          <p style={{
            color: THEME.text.secondary,
            marginBottom: '32px',
            fontSize: '15px',
          }}>
            Sign in to manage Ice Swap
          </p>

          <form onSubmit={handleLogin}>
            <div style={{ position: 'relative', marginBottom: loginError ? '12px' : '24px' }}>
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter admin password"
                style={{
                  width: '100%',
                  padding: '16px 48px 16px 20px',
                  background: THEME.bg.tertiary,
                  border: `1px solid ${THEME.border}`,
                  borderRadius: '12px',
                  color: THEME.text.primary,
                  fontSize: '15px',
                  outline: 'none',
                  transition: 'all 0.2s',
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = THEME.accent.primary;
                  e.target.style.boxShadow = '0 0 0 3px rgba(99, 102, 241, 0.1)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = THEME.border;
                  e.target.style.boxShadow = 'none';
                }}
                autoFocus
              />
              <button
                type="button"
                onClick={() => setShowPassword((s) => !s)}
                style={{
                  position: 'absolute',
                  right: '16px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: THEME.text.muted,
                  padding: '4px',
                }}
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>

            {loginError && (
              <div style={{
                padding: '12px 16px',
                background: 'rgba(239, 68, 68, 0.1)',
                border: '1px solid rgba(239, 68, 68, 0.2)',
                borderRadius: '8px',
                color: THEME.accent.danger,
                fontSize: '14px',
                marginBottom: '20px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
              }}>
                <AlertCircle size={16} />
                {loginError}
              </div>
            )}

            <button
              type="submit"
              disabled={isLoading}
              style={{
                width: '100%',
                padding: '16px',
                background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                border: 'none',
                borderRadius: '12px',
                color: 'white',
                fontSize: '15px',
                fontWeight: '600',
                cursor: isLoading ? 'wait' : 'pointer',
                opacity: isLoading ? 0.7 : 1,
                transition: 'all 0.2s',
                boxShadow: THEME.glow.primary,
              }}
              onMouseEnter={(e) => {
                if (!isLoading) {
                  e.target.style.transform = 'translateY(-2px)';
                  e.target.style.boxShadow = '0 10px 30px rgba(99, 102, 241, 0.4)';
                }
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = THEME.glow.primary;
              }}
            >
              {isLoading ? (
                <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                  <RefreshCw size={16} style={{ animation: 'spin 1s linear infinite' }} />
                  Verifying...
                </span>
              ) : (
                'Access Dashboard'
              )}
            </button>
          </form>

          <Link
            to="/"
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '6px',
              marginTop: '24px',
              color: THEME.text.muted,
              textDecoration: 'none',
              fontSize: '14px',
              transition: 'color 0.2s',
            }}
            onMouseEnter={(e) => e.target.style.color = THEME.text.secondary}
            onMouseLeave={(e) => e.target.style.color = THEME.text.muted}
          >
            <ChevronRight size={16} style={{ transform: 'rotate(180deg)' }} />
            Back to site
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: THEME.bg.primary,
      color: THEME.text.primary,
    }}>
      <header style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        height: '72px',
        background: 'rgba(10, 10, 15, 0.8)',
        backdropFilter: 'blur(20px)',
        borderBottom: `1px solid ${THEME.border}`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '0 32px',
        zIndex: 100,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            style={{
              display: 'none',
              background: 'none',
              border: 'none',
              color: THEME.text.secondary,
              cursor: 'pointer',
              padding: '8px',
            }}
          >
            <Menu size={24} />
          </button>
          <div style={{
            width: '40px',
            height: '40px',
            borderRadius: '10px',
            background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: THEME.glow.primary,
          }}>
            <Snowflake size={20} color="white" />
          </div>
          <div>
            <h1 style={{
              fontSize: '18px',
              fontWeight: '600',
              margin: 0,
              letterSpacing: '-0.3px',
            }}>
              Ice Swap
            </h1>
            <span style={{
              fontSize: '12px',
              color: THEME.text.muted,
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
            }}>
              Admin Dashboard
            </span>
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          {lastUpdated && (
            <span style={{
              fontSize: '13px',
              color: THEME.text.muted,
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
            }}>
              <Clock size={14} />
              Updated {lastUpdated.toLocaleTimeString()}
            </span>
          )}
          
          <button
            onClick={fetchData}
            style={{
              padding: '10px',
              background: THEME.bg.tertiary,
              border: `1px solid ${THEME.border}`,
              borderRadius: '10px',
              color: THEME.text.secondary,
              cursor: 'pointer',
              transition: 'all 0.2s',
            }}
            onMouseEnter={(e) => {
              e.target.style.borderColor = THEME.accent.primary;
              e.target.style.color = THEME.text.primary;
            }}
            onMouseLeave={(e) => {
              e.target.style.borderColor = THEME.border;
              e.target.style.color = THEME.text.secondary;
            }}
            title="Refresh data"
          >
            <RefreshCw size={18} />
          </button>


          <button
            onClick={handleLogout}
            style={{
              padding: '10px 16px',
              background: 'rgba(239, 68, 68, 0.1)',
              border: '1px solid rgba(239, 68, 68, 0.2)',
              borderRadius: '10px',
              color: THEME.accent.danger,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              fontSize: '13px',
              fontWeight: '500',
              transition: 'all 0.2s',
            }}
            onMouseEnter={(e) => {
              e.target.style.background = 'rgba(239, 68, 68, 0.2)';
            }}
            onMouseLeave={(e) => {
              e.target.style.background = 'rgba(239, 68, 68, 0.1)';
            }}
          >
            <LogOut size={16} />
            Logout
          </button>
        </div>
      </header>

      <main style={{
        paddingTop: '72px',
        minHeight: '100vh',
      }}>
        <div style={{
          maxWidth: '1600px',
          margin: '0 auto',
          padding: '32px',
        }}>
          {error && (
            <div style={{
              padding: '16px 20px',
              background: 'rgba(239, 68, 68, 0.1)',
              border: '1px solid rgba(239, 68, 68, 0.2)',
              borderRadius: '12px',
              marginBottom: '24px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}>
              <span style={{ color: THEME.accent.danger, display: 'flex', alignItems: 'center', gap: '8px' }}>
                <AlertCircle size={18} />
                Error: {error}
              </span>
              <button
                onClick={fetchData}
                style={{
                  padding: '8px 16px',
                  background: 'rgba(239, 68, 68, 0.2)',
                  border: 'none',
                  borderRadius: '6px',
                  color: THEME.accent.danger,
                  cursor: 'pointer',
                  fontSize: '13px',
                  fontWeight: '500',
                }}
              >
                Retry
              </button>
            </div>
          )}

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: '20px',
            marginBottom: '32px',
          }}>
            {[
              {
                icon: Database,
                label: 'Total Orders',
                value: stats.total_orders,
                color: THEME.accent.primary,
                glow: THEME.glow.primary,
              },
              {
                icon: DollarSign,
                label: 'Total Quotes',
                value: stats.total_quotes,
                color: THEME.accent.success,
                glow: THEME.glow.success,
              },
              {
                icon: Activity,
                label: 'Active Orders',
                value: orders.filter((o) => isOrderActive(o.status)).length,
                color: THEME.accent.warning,
                glow: THEME.glow.warning,
              },
              {
                icon: CheckCircle2,
                label: 'Completed',
                value: orders.filter((o) => o.status === 'completed').length,
                color: THEME.accent.frost,
                glow: '0 0 20px rgba(103, 232, 249, 0.3)',
              },
            ].map((stat, index) => (
              <div
                key={index}
                style={{
                  padding: '24px',
                  background: THEME.bg.card,
                  backdropFilter: 'blur(10px)',
                  borderRadius: '16px',
                  border: `1px solid ${THEME.border}`,
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '16px',
                  transition: 'all 0.3s',
                  cursor: 'default',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-4px)';
                  e.currentTarget.style.boxShadow = stat.glow;
                  e.currentTarget.style.borderColor = stat.color + '40';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
                  e.currentTarget.style.borderColor = THEME.border;
                }}
              >
                <div style={{
                  width: '48px',
                  height: '48px',
                  borderRadius: '12px',
                  background: stat.color + '15',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: stat.color,
                }}>
                  <stat.icon size={24} />
                </div>
                <div>
                  <div style={{
                    fontSize: '28px',
                    fontWeight: '700',
                    color: THEME.text.primary,
                    marginBottom: '4px',
                  }}>
                    <AnimatedCounter value={stat.value} />
                  </div>
                  <div style={{
                    fontSize: '14px',
                    color: THEME.text.secondary,
                  }}>
                    {stat.label}
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div style={{
            display: 'flex',
            gap: '8px',
            marginBottom: '24px',
            padding: '4px',
            background: THEME.bg.secondary,
            borderRadius: '12px',
            width: 'fit-content',
          }}>
            {[
              { id: 'orders', label: 'Orders', icon: Layers },
              { id: 'volume', label: 'Volume & Stats', icon: BarChart3 },
              { id: 'logs', label: 'API Logs', icon: Activity },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                style={{
                  padding: '12px 20px',
                  background: activeTab === tab.id ? THEME.bg.tertiary : 'transparent',
                  border: 'none',
                  borderRadius: '8px',
                  color: activeTab === tab.id ? THEME.text.primary : THEME.text.secondary,
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  transition: 'all 0.2s',
                  boxShadow: activeTab === tab.id ? '0 4px 12px rgba(0, 0, 0, 0.2)' : 'none',
                }}
              >
                <tab.icon size={16} />
                {tab.label}
                {tab.id === 'orders' && activeTab === 'orders' && (
                  <span style={{
                    width: '6px',
                    height: '6px',
                    borderRadius: '50%',
                    background: THEME.accent.success,
                    animation: 'pulse 2s infinite',
                  }} />
                )}
              </button>
            ))}
          </div>

          <div style={{
            background: THEME.bg.card,
            backdropFilter: 'blur(10px)',
            borderRadius: '20px',
            border: `1px solid ${THEME.border}`,
            overflow: 'hidden',
          }}>
            {loading ? (
              <div style={{
                padding: '80px',
                textAlign: 'center',
                color: THEME.text.secondary,
              }}>
                <RefreshCw size={40} style={{ animation: 'spin 1s linear infinite', marginBottom: '16px' }} />
                <p>Loading data...</p>
              </div>
            ) : (
              <div style={{ padding: '24px' }}>
                {activeTab === 'orders' && (
                  <>
                    <div style={{
                      display: 'flex',
                      gap: '12px',
                      marginBottom: '24px',
                    }}>
                      <div style={{
                        flex: 1,
                        position: 'relative',
                      }}>
                        <Search size={18} style={{
                          position: 'absolute',
                          left: '16px',
                          top: '50%',
                          transform: 'translateY(-50%)',
                          color: THEME.text.muted,
                        }} />
                        <input
                          type="text"
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          placeholder="Search by order ID or short ID..."
                          onKeyPress={(e) => e.key === 'Enter' && searchOrder()}
                          style={{
                            width: '100%',
                            padding: '14px 16px 14px 48px',
                            background: THEME.bg.secondary,
                            border: `1px solid ${THEME.border}`,
                            borderRadius: '12px',
                            color: THEME.text.primary,
                            fontSize: '14px',
                            outline: 'none',
                            transition: 'all 0.2s',
                          }}
                          onFocus={(e) => {
                            e.target.style.borderColor = THEME.accent.primary;
                            e.target.style.boxShadow = '0 0 0 3px rgba(99, 102, 241, 0.1)';
                          }}
                          onBlur={(e) => {
                            e.target.style.borderColor = THEME.border;
                            e.target.style.boxShadow = 'none';
                          }}
                        />
                      </div>
                      <button
                        onClick={searchOrder}
                        style={{
                          padding: '14px 24px',
                          background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                          border: 'none',
                          borderRadius: '12px',
                          color: 'white',
                          fontSize: '14px',
                          fontWeight: '600',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          transition: 'all 0.2s',
                          boxShadow: THEME.glow.primary,
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.transform = 'translateY(-2px)';
                          e.target.style.boxShadow = '0 8px 25px rgba(99, 102, 241, 0.4)';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.transform = 'translateY(0)';
                          e.target.style.boxShadow = THEME.glow.primary;
                        }}
                      >
                        Search
                      </button>
                    </div>

                    {searchError && (
                      <div style={{
                        padding: '12px 16px',
                        background: 'rgba(239, 68, 68, 0.1)',
                        border: '1px solid rgba(239, 68, 68, 0.2)',
                        borderRadius: '8px',
                        color: THEME.accent.danger,
                        marginBottom: '20px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                      }}>
                        <AlertCircle size={16} />
                        {searchError}
                      </div>
                    )}

                    {orders.length === 0 ? (
                      <div style={{
                        padding: '60px',
                        textAlign: 'center',
                        color: THEME.text.muted,
                      }}>
                        <Database size={48} style={{ marginBottom: '16px', opacity: 0.5 }} />
                        <p>No orders found</p>
                      </div>
                    ) : (
                      <div style={{
                        overflowX: 'auto',
                        borderRadius: '12px',
                        border: `1px solid ${THEME.border}`,
                      }}>
                        <table style={{
                          width: '100%',
                          borderCollapse: 'collapse',
                          fontSize: '14px',
                        }}>
                          <thead>
                            <tr style={{
                              background: THEME.bg.secondary,
                              borderBottom: `1px solid ${THEME.border}`,
                            }}>
                              {['Order ID', 'Provider', 'Pair', 'Amount', 'Status', 'Created', 'Actions'].map((header) => (
                                <th
                                  key={header}
                                  style={{
                                    padding: '16px',
                                    textAlign: 'left',
                                    color: THEME.text.secondary,
                                    fontWeight: '500',
                                    fontSize: '12px',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.5px',
                                  }}
                                >
                                  {header}
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {orders.map((order, index) => {
                              const statusConfig = STATUS_CONFIG[order.status] || STATUS_CONFIG.pending;
                              const StatusIcon = statusConfig.icon;
                              const provColor = providerColor(order.provider);
                              const provName = providerLabel(order.provider);
                              const provIcon = providerIcon(order.provider);
                              
                              return (
                                <tr
                                  key={order.id}
                                  style={{
                                    borderBottom: `1px solid ${THEME.border}`,
                                    background: index % 2 === 0 ? 'transparent' : 'rgba(255, 255, 255, 0.02)',
                                    transition: 'background 0.2s',
                                  }}
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.background = 'rgba(99, 102, 241, 0.05)';
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.background = index % 2 === 0 ? 'transparent' : 'rgba(255, 255, 255, 0.02)';
                                  }}
                                >
                                  <td style={{ padding: '16px' }}>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                      <span style={{
                                        fontFamily: 'monospace',
                                        fontSize: '13px',
                                        color: THEME.text.primary,
                                      }}>
                                        {order.id?.slice(0, 8)}...
                                      </span>
                                      <span style={{
                                        fontSize: '11px',
                                        color: THEME.text.muted,
                                      }}>
                                        {order.short_id}
                                      </span>
                                    </div>
                                  </td>
                                  <td style={{ padding: '16px' }}>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                      <span style={{
                                        padding: '4px 10px',
                                        background: provColor + '20',
                                        borderRadius: '6px',
                                        color: provColor,
                                        fontSize: '12px',
                                        fontWeight: '600',
                                        width: 'fit-content',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '4px',
                                      }}>
                                        <span>{provIcon}</span>
                                        {provName}
                                      </span>
                                      <span style={{
                                        fontFamily: 'monospace',
                                        fontSize: '11px',
                                        color: THEME.text.muted,
                                      }}>
                                        ID: {order.provider || 'N/A'}
                                      </span>
                                    </div>
                                  </td>
                                  <td style={{ padding: '16px' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                      <span style={{
                                        padding: '4px 10px',
                                        background: THEME.accent.primary + '20',
                                        borderRadius: '6px',
                                        color: THEME.accent.primary,
                                        fontSize: '12px',
                                        fontWeight: '600',
                                      }}>
                                        {order.from_asset?.toUpperCase()}
                                      </span>
                                      <ArrowUpRight size={14} color={THEME.text.muted} />
                                      <span style={{
                                        padding: '4px 10px',
                                        background: THEME.accent.success + '20',
                                        borderRadius: '6px',
                                        color: THEME.accent.success,
                                        fontSize: '12px',
                                        fontWeight: '600',
                                      }}>
                                        {order.to_asset?.toUpperCase()}
                                      </span>
                                    </div>
                                  </td>
                                  <td style={{ padding: '16px' }}>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                      <span style={{ color: THEME.text.primary, fontWeight: '500' }}>
                                        {order.from_amount} {order.from_asset?.toUpperCase()}
                                      </span>
                                      <span style={{ fontSize: '12px', color: THEME.text.muted }}>
                                        → {order.to_amount} {order.to_asset?.toUpperCase()}
                                      </span>
                                    </div>
                                  </td>
                                  <td style={{ padding: '16px' }}>
                                    <div style={{
                                      display: 'inline-flex',
                                      alignItems: 'center',
                                      gap: '6px',
                                      padding: '6px 12px',
                                      background: statusConfig.color + '15',
                                      borderRadius: '20px',
                                      border: `1px solid ${statusConfig.color}30`,
                                      color: statusConfig.color,
                                      fontSize: '12px',
                                      fontWeight: '600',
                                      boxShadow: isOrderActive(order.status) ? statusConfig.glow : 'none',
                                    }}>
                                      <StatusIcon size={12} />
                                      {statusConfig.label}
                                      {isOrderActive(order.status) && (
                                        <span style={{
                                          width: '6px',
                                          height: '6px',
                                          borderRadius: '50%',
                                          background: statusConfig.color,
                                          animation: 'pulse 2s infinite',
                                        }} />
                                      )}
                                    </div>
                                  </td>
                                  <td style={{ padding: '16px', color: THEME.text.secondary, fontSize: '13px' }}>
                                    {order.created_at ? new Date(order.created_at).toLocaleDateString() : 'N/A'}
                                  </td>
                                  <td style={{ padding: '16px' }}>
                                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                                      <button
                                        onClick={() => viewOrderDetails(order.id)}
                                        style={{
                                          padding: '8px 14px',
                                          background: THEME.bg.tertiary,
                                          border: `1px solid ${THEME.border}`,
                                          borderRadius: '8px',
                                          color: THEME.text.secondary,
                                          fontSize: '12px',
                                          cursor: 'pointer',
                                          display: 'flex',
                                          alignItems: 'center',
                                          gap: '6px',
                                          transition: 'all 0.2s',
                                        }}
                                        onMouseEnter={(e) => {
                                          e.target.style.borderColor = THEME.accent.primary;
                                          e.target.style.color = THEME.text.primary;
                                        }}
                                        onMouseLeave={(e) => {
                                          e.target.style.borderColor = THEME.border;
                                          e.target.style.color = THEME.text.secondary;
                                        }}
                                      >
                                        <Eye size={14} />
                                        View
                                      </button>
                                      
                                      <select
                                        value=""
                                        onChange={(e) => changeOrderStatus(order.id, e.target.value, e)}
                                        style={{
                                          padding: '8px 12px',
                                          background: THEME.bg.tertiary,
                                          border: `1px solid ${THEME.border}`,
                                          borderRadius: '8px',
                                          color: THEME.text.secondary,
                                          fontSize: '12px',
                                          cursor: 'pointer',
                                          outline: 'none',
                                        }}
                                      >
                                        <option value="">Change Status...</option>
                                        <option value="pending">Pending</option>
                                        <option value="confirmed">Confirmed</option>
                                        <option value="processing">Processing</option>
                                        <option value="completed">Completed</option>
                                        <option value="canceled">Canceled</option>
                                        <option value="refunded">Refunded</option>
                                        <option value="failed">Failed</option>
                                        <option value="archived">Archived</option>
                                      </select>
                                      
                                      <button
                                        onClick={(e) => deleteOrder(order.id, e)}
                                        style={{
                                          padding: '8px 14px',
                                          background: 'rgba(239, 68, 68, 0.1)',
                                          border: '1px solid rgba(239, 68, 68, 0.2)',
                                          borderRadius: '8px',
                                          color: THEME.accent.danger,
                                          fontSize: '12px',
                                          cursor: 'pointer',
                                          display: 'flex',
                                          alignItems: 'center',
                                          gap: '6px',
                                          transition: 'all 0.2s',
                                        }}
                                        onMouseEnter={(e) => {
                                          e.target.style.background = 'rgba(239, 68, 68, 0.2)';
                                        }}
                                        onMouseLeave={(e) => {
                                          e.target.style.background = 'rgba(239, 68, 68, 0.1)';
                                        }}
                                      >
                                        <XCircle size={14} />
                                        Delete
                                      </button>
                                    </div>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </>
                )}

                {activeTab === 'volume' && (
                  <div>
                    {!volumeStats ? (
                      <div style={{
                        padding: '60px',
                        textAlign: 'center',
                        color: THEME.text.muted,
                      }}>
                        <BarChart3 size={48} style={{ marginBottom: '16px', opacity: 0.5 }} />
                        <p>Volume statistics not available</p>
                      </div>
                    ) : (
                      <>
                        <div style={{
                          display: 'grid',
                          gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                          gap: '16px',
                          marginBottom: '32px',
                        }}>
                          {[
                            { label: '24h Volume', value: volumeStats['24h']?.volume_usd, count: volumeStats['24h']?.count, color: THEME.accent.success, icon: TrendingUp },
                            { label: '7d Volume', value: volumeStats['7d']?.volume_usd, count: volumeStats['7d']?.count, color: THEME.accent.primary, icon: Calendar },
                            { label: '30d Volume', value: volumeStats['30d']?.volume_usd, count: volumeStats['30d']?.count, color: THEME.accent.secondary, icon: BarChart3 },
                            { label: 'All Time', value: volumeStats.all_time?.volume_usd, count: volumeStats.all_time?.count, color: THEME.accent.frost, icon: Database },
                          ].map((stat, index) => (
                            <div
                              key={index}
                              style={{
                                padding: '20px',
                                background: THEME.bg.secondary,
                                borderRadius: '12px',
                                border: `1px solid ${THEME.border}`,
                              }}
                            >
                              <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                marginBottom: '12px',
                                color: stat.color,
                              }}>
                                <stat.icon size={18} />
                                <span style={{ fontSize: '12px', fontWeight: '600', textTransform: 'uppercase' }}>
                                  {stat.label}
                                </span>
                              </div>
                              <div style={{
                                fontSize: '24px',
                                fontWeight: '700',
                                color: THEME.text.primary,
                                marginBottom: '4px',
                              }}>
                                {formatCurrency(stat.value)}
                              </div>
                              <div style={{
                                fontSize: '13px',
                                color: THEME.text.muted,
                              }}>
                                {stat.count || 0} swaps
                              </div>
                            </div>
                          ))}
                        </div>

                        {volumeStats.by_asset && volumeStats.by_asset.length > 0 && (
                          <>
                            <h3 style={{
                              fontSize: '16px',
                              fontWeight: '600',
                              marginBottom: '16px',
                              color: THEME.text.primary,
                            }}>
                              Top Assets (30d)
                            </h3>
                            <div style={{
                              display: 'flex',
                              flexDirection: 'column',
                              gap: '12px',
                              marginBottom: '32px',
                            }}>
                              {volumeStats.by_asset.map((asset, index) => (
                                <div
                                  key={asset.from_asset}
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '16px',
                                    padding: '16px',
                                    background: THEME.bg.secondary,
                                    borderRadius: '12px',
                                    border: `1px solid ${THEME.border}`,
                                  }}
                                >
                                  <div style={{
                                    width: '32px',
                                    height: '32px',
                                    borderRadius: '8px',
                                    background: `linear-gradient(135deg, ${THEME.accent.primary} 0%, ${THEME.accent.secondary} 100%)`,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: '12px',
                                    fontWeight: '700',
                                    color: 'white',
                                  }}>
                                    #{index + 1}
                                  </div>
                                  <div style={{ flex: 1 }}>
                                    <div style={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'space-between',
                                      marginBottom: '8px',
                                    }}>
                                      <span style={{
                                        fontSize: '14px',
                                        fontWeight: '600',
                                        color: THEME.text.primary,
                                      }}>
                                        {asset.from_asset?.toUpperCase()}
                                      </span>
                                      <span style={{
                                        fontSize: '14px',
                                        color: THEME.text.secondary,
                                      }}>
                                        {formatCurrency(asset.volume_usd)}
                                      </span>
                                    </div>
                                    <div style={{
                                      height: '6px',
                                      background: THEME.bg.tertiary,
                                      borderRadius: '3px',
                                      overflow: 'hidden',
                                    }}>
                                      <div style={{
                                        height: '100%',
                                        width: `${(asset.volume_usd / (volumeStats.by_asset[0]?.volume_usd || 1)) * 100}%`,
                                        background: `linear-gradient(90deg, ${THEME.accent.primary} 0%, ${THEME.accent.secondary} 100%)`,
                                        borderRadius: '3px',
                                        transition: 'width 0.5s ease',
                                      }} />
                                    </div>
                                  </div>
                                  <div style={{
                                    fontSize: '12px',
                                    color: THEME.text.muted,
                                    minWidth: '60px',
                                    textAlign: 'right',
                                  }}>
                                    {asset.count} swaps
                                  </div>
                                </div>
                              ))}
                            </div>
                          </>
                        )}

                        {volumeStats.daily_chart && volumeStats.daily_chart.length > 0 && (
                          <>
                            <h3 style={{
                              fontSize: '16px',
                              fontWeight: '600',
                              marginBottom: '16px',
                              color: THEME.text.primary,
                            }}>
                              Daily Volume (Last 30 Days)
                            </h3>
                            <div style={{
                              height: '250px',
                              display: 'flex',
                              alignItems: 'flex-end',
                              gap: '8px',
                              padding: '20px',
                              background: THEME.bg.secondary,
                              borderRadius: '12px',
                              border: `1px solid ${THEME.border}`,
                            }}>
                              {volumeStats.daily_chart.map((day, index) => {
                                const maxVolume = Math.max(...volumeStats.daily_chart.map((d) => d.volume_usd || 0), 1);
                                const height = maxVolume > 0 ? ((day.volume_usd || 0) / maxVolume) * 100 : 0;
                                return (
                                  <div
                                    key={day.day}
                                    style={{
                                      flex: 1,
                                      display: 'flex',
                                      flexDirection: 'column',
                                      alignItems: 'center',
                                      gap: '8px',
                                      position: 'relative',
                                    }}
                                  >
                                    <div
                                      style={{
                                        width: '100%',
                                        height: `${height}%`,
                                        background: `linear-gradient(180deg, ${THEME.accent.primary} 0%, ${THEME.accent.secondary} 100%)`,
                                        borderRadius: '4px 4px 0 0',
                                        minHeight: '4px',
                                        transition: 'height 0.5s ease',
                                        cursor: 'pointer',
                                      }}
                                      title={`${day.day}: ${formatCurrency(day.volume_usd)} (${day.count} swaps)`}
                                    />
                                    <span style={{
                                      fontSize: '10px',
                                      color: THEME.text.muted,
                                      transform: 'rotate(-45deg)',
                                      whiteSpace: 'nowrap',
                                    }}>
                                      {day.day?.slice(5)}
                                    </span>
                                  </div>
                                );
                              })}
                            </div>
                          </>
                        )}
                      </>
                    )}
                  </div>
                )}

                {activeTab === 'logs' && (
                  <div>
                    {logs.length === 0 ? (
                      <div style={{
                        padding: '60px',
                        textAlign: 'center',
                        color: THEME.text.muted,
                      }}>
                        <Activity size={48} style={{ marginBottom: '16px', opacity: 0.5 }} />
                        <p>No logs found</p>
                      </div>
                    ) : (
                      <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '8px',
                      }}>
                        {logs.map((log) => (
                          <div
                            key={log.id}
                            style={{
                              padding: '16px',
                              background: THEME.bg.secondary,
                              borderRadius: '10px',
                              border: `1px solid ${THEME.border}`,
                              display: 'flex',
                              alignItems: 'center',
                              gap: '16px',
                            }}
                          >
                            <span style={{
                              padding: '4px 10px',
                              background: log.status_code === 200 ? THEME.accent.success + '20' : THEME.accent.danger + '20',
                              borderRadius: '6px',
                              color: log.status_code === 200 ? THEME.accent.success : THEME.accent.danger,
                              fontSize: '12px',
                              fontWeight: '600',
                              fontFamily: 'monospace',
                            }}>
                              {log.method}
                            </span>
                            <span style={{
                              flex: 1,
                              fontSize: '13px',
                              color: THEME.text.secondary,
                              fontFamily: 'monospace',
                            }}>
                              {log.endpoint}
                            </span>
                            <span style={{
                              padding: '4px 10px',
                              borderRadius: '6px',
                              fontSize: '12px',
                              fontWeight: '600',
                              background: log.status_code === 200 ? THEME.accent.success + '20' : THEME.accent.danger + '20',
                              color: log.status_code === 200 ? THEME.accent.success : THEME.accent.danger,
                            }}>
                              {log.status_code}
                            </span>
                            <span style={{
                              fontSize: '12px',
                              color: THEME.text.muted,
                            }}>
                              {log.timestamp ? new Date(log.timestamp).toLocaleTimeString() : 'N/A'}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </main>

      {selectedOrder && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0, 0, 0, 0.8)',
            backdropFilter: 'blur(8px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '20px',
          }}
          onClick={() => setSelectedOrder(null)}
        >
          <div
            style={{
              width: '100%',
              maxWidth: '600px',
              maxHeight: '90vh',
              overflow: 'auto',
              background: THEME.bg.secondary,
              borderRadius: '20px',
              border: `1px solid ${THEME.border}`,
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{
              padding: '24px',
              borderBottom: `1px solid ${THEME.border}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <h2 style={{
                  fontSize: '20px',
                  fontWeight: '600',
                  margin: 0,
                }}>
                  Order Details
                </h2>
                {isOrderActive(selectedOrder.status) && (
                  <span style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '4px 10px',
                    background: THEME.accent.success + '15',
                    borderRadius: '20px',
                    color: THEME.accent.success,
                    fontSize: '11px',
                    fontWeight: '600',
                    textTransform: 'uppercase',
                  }}>
                    <span style={{
                      width: '6px',
                      height: '6px',
                      borderRadius: '50%',
                      background: THEME.accent.success,
                      animation: 'pulse 2s infinite',
                    }} />
                    Live
                  </span>
                )}
              </div>
              <button
                onClick={() => setSelectedOrder(null)}
                style={{
                  width: '36px',
                  height: '36px',
                  borderRadius: '10px',
                  background: THEME.bg.tertiary,
                  border: `1px solid ${THEME.border}`,
                  color: THEME.text.secondary,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.2s',
                }}
                onMouseEnter={(e) => {
                  e.target.style.borderColor = THEME.accent.danger;
                  e.target.style.color = THEME.accent.danger;
                }}
                onMouseLeave={(e) => {
                  e.target.style.borderColor = THEME.border;
                  e.target.style.color = THEME.text.secondary;
                }}
              >
                <X size={18} />
              </button>
            </div>

            <div style={{ padding: '24px' }}>
              <div style={{
                padding: '16px',
                background: (STATUS_CONFIG[selectedOrder.status]?.color || THEME.accent.primary) + '10',
                borderRadius: '12px',
                border: `1px solid ${(STATUS_CONFIG[selectedOrder.status]?.color || THEME.accent.primary)}30`,
                marginBottom: '24px',
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: '12px',
                  flexWrap: 'wrap',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    {React.createElement(STATUS_CONFIG[selectedOrder.status]?.icon || Clock, {
                      size: 24,
                      color: STATUS_CONFIG[selectedOrder.status]?.color || THEME.accent.primary,
                    })}
                    <div>
                      <div style={{
                        fontSize: '12px',
                        color: THEME.text.muted,
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                        marginBottom: '4px',
                      }}>
                        Current Status
                      </div>
                      <div style={{
                        fontSize: '18px',
                        fontWeight: '600',
                        color: STATUS_CONFIG[selectedOrder.status]?.color || THEME.text.primary,
                      }}>
                        {(STATUS_CONFIG[selectedOrder.status]?.label || selectedOrder.status || 'Unknown')}
                      </div>
                    </div>
                  </div>
                  
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <select
                      value={selectedOrder.status}
                      onChange={(e) => changeOrderStatus(selectedOrder.id, e.target.value)}
                      style={{
                        padding: '10px 16px',
                        background: THEME.bg.tertiary,
                        border: `1px solid ${THEME.border}`,
                        borderRadius: '8px',
                        color: THEME.text.primary,
                        fontSize: '14px',
                        cursor: 'pointer',
                      }}
                    >
                      <option value="pending">Pending</option>
                      <option value="confirmed">Confirmed</option>
                      <option value="processing">Processing</option>
                      <option value="completed">Completed</option>
                      <option value="canceled">Canceled</option>
                      <option value="refunded">Refunded</option>
                      <option value="failed">Failed</option>
                      <option value="archived">Archived</option>
                    </select>
                    
                    <button
                      onClick={() => deleteOrder(selectedOrder.id)}
                      style={{
                        padding: '10px 16px',
                        background: 'rgba(239, 68, 68, 0.1)',
                        border: '1px solid rgba(239, 68, 68, 0.2)',
                        borderRadius: '8px',
                        color: THEME.accent.danger,
                        fontSize: '14px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                      }}
                    >
                      <XCircle size={16} />
                      Delete
                    </button>
                  </div>
                </div>
              </div>

              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(2, 1fr)',
                gap: '16px',
                marginBottom: '24px',
              }}>
                {[
                  { label: 'Order ID', value: selectedOrder.id, mono: true },
                  { label: 'Short ID', value: selectedOrder.short_id },
                  { 
                    label: 'Provider', 
                    value: (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                        <span style={{
                          padding: '6px 14px',
                          background: providerColor(selectedOrder.provider) + '20',
                          borderRadius: '8px',
                          color: providerColor(selectedOrder.provider),
                          fontSize: '14px',
                          fontWeight: '600',
                          width: 'fit-content',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                        }}>
                          <span>{providerIcon(selectedOrder.provider)}</span>
                          {providerLabel(selectedOrder.provider)}
                        </span>
                        <span style={{
                          fontFamily: 'monospace',
                          fontSize: '12px',
                          color: THEME.text.muted,
                        }}>
                          Provider ID: {selectedOrder.provider || 'N/A'}
                        </span>
                      </div>
                    ), 
                    mono: false 
                  },
                  { label: 'From', value: `${selectedOrder.from_amount} ${selectedOrder.from_asset?.toUpperCase()}` },
                  { label: 'To', value: `${selectedOrder.to_amount} ${selectedOrder.to_asset?.toUpperCase()}` },
                  { label: 'Rate', value: selectedOrder.rate ? `1 ${selectedOrder.from_asset?.toUpperCase()} = ${selectedOrder.rate} ${selectedOrder.to_asset?.toUpperCase()}` : 'N/A' },
                  { label: 'Created', value: selectedOrder.created_at ? new Date(selectedOrder.created_at).toLocaleString() : 'N/A' },
                ].map((item, index) => (
                  <div
                    key={index}
                    style={{
                      padding: '16px',
                      background: THEME.bg.tertiary,
                      borderRadius: '10px',
                      border: `1px solid ${THEME.border}`,
                    }}
                  >
                    <div style={{
                      fontSize: '11px',
                      color: THEME.text.muted,
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      marginBottom: '6px',
                    }}>
                      {item.label}
                    </div>
                    <div style={{
                      fontSize: '14px',
                      color: THEME.text.primary,
                      fontWeight: '500',
                      fontFamily: item.mono ? 'monospace' : 'inherit',
                      wordBreak: 'break-all',
                    }}>
                      {item.value || 'N/A'}
                    </div>
                  </div>
                ))}
              </div>

              <div style={{ marginBottom: '24px' }}>
                <h4 style={{
                  fontSize: '12px',
                  color: THEME.text.muted,
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  marginBottom: '12px',
                }}>
                  Addresses
                </h4>
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '12px',
                }}>
                  <div style={{
                    padding: '16px',
                    background: THEME.bg.tertiary,
                    borderRadius: '10px',
                    border: `1px solid ${THEME.border}`,
                  }}>
                    <div style={{
                      fontSize: '11px',
                      color: THEME.text.muted,
                      marginBottom: '6px',
                    }}>
                      Deposit Address
                    </div>
                    <div style={{
                      fontFamily: 'monospace',
                      fontSize: '13px',
                      color: THEME.text.primary,
                      wordBreak: 'break-all',
                    }}>
                      {selectedOrder.deposit_address || 'N/A'}
                    </div>
                  </div>
                  <div style={{
                    padding: '16px',
                    background: THEME.bg.tertiary,
                    borderRadius: '10px',
                    border: `1px solid ${THEME.border}`,
                  }}>
                    <div style={{
                      fontSize: '11px',
                      color: THEME.text.muted,
                      marginBottom: '6px',
                    }}>
                      Destination Address
                    </div>
                    <div style={{
                      fontFamily: 'monospace',
                      fontSize: '13px',
                      color: THEME.text.primary,
                      wordBreak: 'break-all',
                    }}>
                      {selectedOrder.destination_address || 'N/A'}
                    </div>
                  </div>
                </div>
              </div>

              {selectedOrder.raw_response && (
                <div>
                  <h4 style={{
                    fontSize: '12px',
                    color: THEME.text.muted,
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    marginBottom: '12px',
                  }}>
                    Raw Response
                  </h4>
                  <pre style={{
                    padding: '16px',
                    background: THEME.bg.tertiary,
                    borderRadius: '10px',
                    border: `1px solid ${THEME.border}`,
                    fontSize: '12px',
                    color: THEME.text.secondary,
                    overflow: 'auto',
                    maxHeight: '200px',
                    margin: 0,
                  }}>
                    {(() => {
                      try {
                        return JSON.stringify(JSON.parse(selectedOrder.raw_response), null, 2);
                      } catch {
                        return selectedOrder.raw_response;
                      }
                    })()}
                  </pre>
                </div>
              )}
            </div>

            <div style={{
              padding: '20px 24px',
              borderTop: `1px solid ${THEME.border}`,
              display: 'flex',
              justifyContent: 'flex-end',
            }}>
              <button
                onClick={() => setSelectedOrder(null)}
                style={{
                  padding: '12px 24px',
                  background: THEME.bg.tertiary,
                  border: `1px solid ${THEME.border}`,
                  borderRadius: '10px',
                  color: THEME.text.primary,
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                }}
                onMouseEnter={(e) => {
                  e.target.style.borderColor = THEME.accent.primary;
                }}
                onMouseLeave={(e) => {
                  e.target.style.borderColor = THEME.border;
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
      `}</style>
    </div>
  );
};

export default AdminDashboard;